import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowRight, Copy, Download, Edit2, Save, Loader2, Sparkles, Undo2, RefreshCw, LetterText, FileText, Check, Info, ArrowUpRight, History, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import nexisLogo from "@/assets/nexis-logo-new.png";
import { Json } from "@/integrations/supabase/types";
import { useCredits } from "@/hooks/useCredits";
import { CreditCostBadge } from "@/components/CreditGate";
import { NexisBreadcrumb } from "@/components/nexis/NexisBreadcrumb";

interface IdeaData {
  title?: string;
  hook?: string;
  emotion?: string;
  retentionStrategy?: string;
  viralityScore?: number;
  summary?: string;
}

interface Project {
  id: string;
  title: string;
  script: string | null;
  idea_data: Json | null;
  platform: string | null;
  length: string | null;
  niche: string | null;
}

interface ScriptVersion {
  id: string;
  version_number: number;
  script_content: string;
  label: string | null;
  created_at: string;
}

// Extract only narration from script
const extractNarration = (script: string): string => {
  if (!script || !script.trim()) return "No script content available.";
  
  const lines = script.split("\n");
  const narrationLines: string[] = [];

  for (const line of lines) {
    let text = line.trim();
    if (!text) continue;
    if (text.match(/^\[.*\]$/)) continue;
    if (text.match(/^\d{1,2}:\d{2}/)) continue;
    if (text.match(/^\*?\*?SCENE\s*\d+/i)) continue;
    if (text.match(/^(Visual|Music|SFX|Sound|CUT TO|FADE|INT\.|EXT\.)[:\s]/i)) continue;
    if (text.match(/^#+\s/)) continue;
    if (text === text.toUpperCase() && text.length < 30 && !/\s/.test(text)) continue;
    text = text.replace(/\[[^\]]*\]/g, "").trim();
    text = text.replace(/^\*?\*?(Narrator|Host|VO|Voiceover|Speaker|Narration)[^:]*\*?\*?:\s*/i, "");
    text = text.replace(/\*/g, "");
    text = text.replace(/^["']/, "").replace(/["']$/, "");
    if (!text || text.length < 3) continue;
    narrationLines.push(text);
  }

  const result = narrationLines.join("\n\n").trim();
  if (!result) return script.trim();
  return result;
};

const SUGGESTED_EDITS = [
  "Make the hook stronger",
  "Add a call to action",
  "Make it shorter",
  "Add more emotion",
  "Include a product mention",
];

export default function NexisProjectScript() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { consume } = useCredits();
  
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedScript, setEditedScript] = useState("");
  const [narrationOnly, setNarrationOnly] = useState(false);
  const [aiEditPrompt, setAiEditPrompt] = useState("");
  const [isAiEditing, setIsAiEditing] = useState(false);
  const [previousScript, setPreviousScript] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  // Versioning state
  const [versions, setVersions] = useState<ScriptVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string>("");
  
  // Title & Description generation state
  interface TitleOption {
    title: string;
    strategy: string;
    reasoning: string;
  }
  const [generatedTitles, setGeneratedTitles] = useState<TitleOption[]>([]);
  const [isGeneratingTitles, setIsGeneratingTitles] = useState(false);
  const [generatedDescription, setGeneratedDescription] = useState("");
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  
  // Collapsible state for generated content
  const [titlesOpen, setTitlesOpen] = useState(false);
  const [descriptionOpen, setDescriptionOpen] = useState(false);

  // Content brief state
  const [contentBrief, setContentBrief] = useState<any>(null);
  const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchProject();
      fetchVersions();
    }
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const { data, error } = await supabase
        .from("nexis_projects")
        .select("id, title, script, idea_data, platform, length, niche")
        .eq("id", projectId)
        .single();

      if (error) throw error;
      setProject(data);
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project");
      navigate("/app/nexis-ai");
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const { data, error } = await supabase
        .from("nexis_script_versions")
        .select("*")
        .eq("project_id", projectId)
        .order("version_number", { ascending: false });
      
      if (!error && data) {
        setVersions(data as ScriptVersion[]);
      }
    } catch (e) {
      console.error("Error fetching versions:", e);
    }
  };

  const saveVersion = async (scriptContent: string, label?: string) => {
    if (!projectId) return;
    const nextVersion = versions.length > 0 ? versions[0].version_number + 1 : 1;
    try {
      await supabase.from("nexis_script_versions").insert({
        project_id: projectId,
        version_number: nextVersion,
        script_content: scriptContent,
        label: label || `v${nextVersion}`,
      });
      await fetchVersions();
    } catch (e) {
      console.error("Error saving version:", e);
    }
  };

  const restoreVersion = async (version: ScriptVersion) => {
    if (!project) return;
    // Save current as a version first
    if (project.script) {
      await saveVersion(project.script, "Before restore");
    }
    
    try {
      const { error } = await supabase
        .from("nexis_projects")
        .update({ script: version.script_content })
        .eq("id", projectId);
      if (error) throw error;
      setProject((prev) => prev ? { ...prev, script: version.script_content } : null);
      setSelectedVersionId("");
      toast.success(`Restored to v${version.version_number}`);
    } catch (e) {
      toast.error("Failed to restore version");
    }
  };

  const handleRegenerateScript = async () => {
    if (!project?.idea_data) {
      toast.error("No idea data available for regeneration");
      return;
    }
    
    setIsRegenerating(true);
    try {
      const credited = await consume("generate-script", "Regenerate script");
      if (!credited) { setIsRegenerating(false); return; }
      
      // Save current version
      if (project.script) await saveVersion(project.script, "Before regeneration");
      setPreviousScript(project.script);
      
      const ideaData = project.idea_data as IdeaData;
      
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "generate-script",
          idea: {
            title: ideaData.title || project.title,
            hook: ideaData.hook || "",
            emotion: ideaData.emotion || "",
            retentionStrategy: ideaData.retentionStrategy || ""
          },
          platform: project.platform || "YouTube",
          length: project.length || "3 minutes"
        }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      const newScript = data.result;
      
      const { error: updateError } = await supabase
        .from("nexis_projects")
        .update({ script: newScript })
        .eq("id", projectId);

      if (updateError) throw updateError;

      setProject((prev) => prev ? { ...prev, script: newScript } : null);
      await saveVersion(newScript, "Regenerated");
      toast.success("Script regenerated successfully");
    } catch (error) {
      console.error("Error regenerating script:", error);
      toast.error("Failed to regenerate script");
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCopyScript = () => {
    if (project?.script) {
      const content = narrationOnly ? extractNarration(project.script) : project.script;
      navigator.clipboard.writeText(content);
      toast.success("Script copied to clipboard");
    }
  };

  const handleDownloadScript = () => {
    if (project?.script) {
      const content = narrationOnly ? extractNarration(project.script) : project.script;
      const blob = new Blob([content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.title}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Script downloaded");
    }
  };

  const handleSaveEdit = async () => {
    try {
      if (project?.script) await saveVersion(project.script, "Before edit");
      setPreviousScript(project?.script || null);
      
      const { error } = await supabase
        .from("nexis_projects")
        .update({ script: editedScript })
        .eq("id", projectId);

      if (error) throw error;
      
      setProject((prev) => prev ? { ...prev, script: editedScript } : null);
      setIsEditing(false);
      await saveVersion(editedScript, "Manual edit");
      toast.success("Script updated");
    } catch (error) {
      console.error("Error saving script:", error);
      toast.error("Failed to save script");
    }
  };

  const handleAiEdit = async () => {
    if (!aiEditPrompt.trim() || !project?.script) return;
    
    setIsAiEditing(true);
    try {
      const credited = await consume("ai-script-edit", "AI script edit");
      if (!credited) { setIsAiEditing(false); return; }
      
      if (project.script) await saveVersion(project.script, "Before AI edit");
      setPreviousScript(project.script);
      
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "edit-script",
          script: project.script,
          editPrompt: aiEditPrompt.trim()
        }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      const newScript = data.result;
      
      const { error: updateError } = await supabase
        .from("nexis_projects")
        .update({ script: newScript })
        .eq("id", projectId);

      if (updateError) throw updateError;

      setProject((prev) => prev ? { ...prev, script: newScript } : null);
      setAiEditPrompt("");
      await saveVersion(newScript, `AI edit: ${aiEditPrompt.trim().slice(0, 40)}`);
      toast.success("Script updated with AI");
    } catch (error) {
      console.error("Error with AI edit:", error);
      toast.error("Failed to apply AI edit");
    } finally {
      setIsAiEditing(false);
    }
  };

  const handleUndo = async () => {
    if (!previousScript) return;
    
    try {
      const { error } = await supabase
        .from("nexis_projects")
        .update({ script: previousScript })
        .eq("id", projectId);

      if (error) throw error;
      
      setProject((prev) => prev ? { ...prev, script: previousScript } : null);
      setPreviousScript(null);
      toast.success("Reverted to previous version");
    } catch (error) {
      console.error("Error undoing:", error);
      toast.error("Failed to undo");
    }
  };

  const handleGenerateTitles = async () => {
    if (!project?.script) return;
    setIsGeneratingTitles(true);
    try {
      const credited = await consume("generate-title", "Generate titles");
      if (!credited) { setIsGeneratingTitles(false); return; }
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "generate-titles",
          script: project.script,
          title: project.title,
          platform: project.platform || "YouTube",
        },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      setGeneratedTitles(data.result);
      setTitlesOpen(true);
      toast.success("Titles generated!");
    } catch (error) {
      console.error("Error generating titles:", error);
      toast.error("Failed to generate titles");
    } finally {
      setIsGeneratingTitles(false);
    }
  };

  const handleGenerateDescription = async () => {
    if (!project?.script) return;
    setIsGeneratingDescription(true);
    try {
      const credited = await consume("generate-description", "Generate description");
      if (!credited) { setIsGeneratingDescription(false); return; }
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "generate-description",
          script: project.script,
          title: project.title,
          platform: project.platform || "YouTube",
        },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      setGeneratedDescription(data.result);
      setDescriptionOpen(true);
      toast.success("Description generated!");
    } catch (error) {
      console.error("Error generating description:", error);
      toast.error("Failed to generate description");
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleCopyTitle = (title: string) => {
    navigator.clipboard.writeText(title);
    toast.success("Title copied!");
  };

  const handleCopyDescription = () => {
    navigator.clipboard.writeText(generatedDescription);
    toast.success("Description copied!");
  };

  const handleGenerateBrief = async () => {
    if (!project) return;
    setIsGeneratingBrief(true);
    try {
      const credited = await consume("generate-script", "Generate content brief");
      if (!credited) { setIsGeneratingBrief(false); return; }
      
      const ideaData = project.idea_data as IdeaData;
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "content-brief",
          title: ideaData?.title || project.title,
          niche: project.niche || "",
          platform: project.platform || "YouTube",
        },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      setContentBrief(data.result);
      toast.success("Content brief generated!");
    } catch (error: any) {
      console.error("Error generating brief:", error);
      toast.error(error.message || "Failed to generate brief");
    } finally {
      setIsGeneratingBrief(false);
    }
  };

  // Word count and estimated video length
  const wordCount = project?.script ? project.script.split(/\s+/).filter(Boolean).length : 0;
  const estimatedMinutes = wordCount / 150;
  const estMinStr = estimatedMinutes < 1
    ? `${Math.round(estimatedMinutes * 60)}s`
    : `${Math.floor(estimatedMinutes)}:${String(Math.round((estimatedMinutes % 1) * 60)).padStart(2, "0")}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <NexisBreadcrumb projectTitle={project?.title} currentStep="Script" />

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <img src={nexisLogo} alt="Nexis AI" className="w-10 h-10 object-contain" />
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold">{project?.title}</h1>
            {project?.platform && (
              <p className="text-sm text-muted-foreground">{project.platform} • {project.length || "Custom length"}</p>
            )}
          </div>
        </div>

        <Tabs defaultValue="script" className="space-y-6">
          <TabsList>
            <TabsTrigger value="script">Script</TabsTrigger>
            <TabsTrigger value="research">Research</TabsTrigger>
          </TabsList>

          {/* Research / Content Brief Tab */}
          <TabsContent value="research">
            <Card className="p-6 bg-card/50 border-border/50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-primary" />
                  <h3 className="font-display font-semibold text-lg">Content Brief</h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGenerateBrief}
                  disabled={isGeneratingBrief}
                >
                  {isGeneratingBrief ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" />{contentBrief ? "Regenerate" : "Generate Brief"}</>
                  )}
                </Button>
              </div>
              
              {!contentBrief && !isGeneratingBrief && (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground mb-1">No content brief yet</p>
                  <p className="text-sm text-muted-foreground">
                    Generate a brief to see competing video titles, suggested hooks, and keyword context for this topic.
                  </p>
                </div>
              )}

              {contentBrief && (
                <div className="space-y-6">
                  {/* Keyword Context */}
                  {contentBrief.keyword && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Target Keyword</h4>
                      <Badge variant="outline" className="text-sm py-1 px-3">{contentBrief.keyword}</Badge>
                    </div>
                  )}

                  {/* Competing Videos */}
                  {contentBrief.competingVideos && contentBrief.competingVideos.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Top Competing Videos</h4>
                      <div className="space-y-2">
                        {contentBrief.competingVideos.map((v: any, i: number) => (
                          <div key={i} className="flex items-start justify-between p-3 rounded-lg bg-background/50 border border-border/50">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{v.title}</p>
                              {v.views && <p className="text-xs text-muted-foreground">{v.views} views</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Suggested Angles/Hooks */}
                  {contentBrief.suggestedHooks && contentBrief.suggestedHooks.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Suggested Hooks & Angles</h4>
                      <div className="space-y-2">
                        {contentBrief.suggestedHooks.map((hook: string, i: number) => (
                          <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-background/50 border border-border/50">
                            <span className="text-primary font-bold text-sm">{i + 1}.</span>
                            <p className="text-sm">{hook}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Summary */}
                  {contentBrief.summary && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Research Summary</h4>
                      <p className="text-sm text-foreground/80 leading-relaxed">{contentBrief.summary}</p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Script Tab */}
          <TabsContent value="script" className="space-y-6">
            <Card className="p-6 bg-card/50 border-border/50">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-semibold">{project?.title}</h2>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="narration-only"
                      checked={narrationOnly}
                      onCheckedChange={setNarrationOnly}
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="narration-only" className="text-sm text-muted-foreground cursor-pointer flex items-center gap-1">
                          Narration Only
                          <Info className="w-3.5 h-3.5" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[250px]">
                        <p className="text-xs">Toggle on to strip scene directions and show only the spoken script — perfect for copying into a teleprompter or voiceover tool.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleRegenerateScript}
                      disabled={isRegenerating || !project?.idea_data}
                    >
                      {isRegenerating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      {isRegenerating ? "Regenerating..." : "Regenerate"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleCopyScript}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleDownloadScript}>
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        setEditedScript(project?.script || "");
                        setIsEditing(true);
                      }}
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                </div>
              </div>

              {/* Word count & estimated length */}
              {project?.script && (
                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4 px-1">
                  <span>Word count: <span className="text-foreground font-medium">{wordCount.toLocaleString()}</span></span>
                  <span>•</span>
                  <span>Estimated length: <span className="text-foreground font-medium">{estMinStr}</span></span>
                  <span className="text-muted-foreground/50">(@150 wpm spoken)</span>
                </div>
              )}
              
              {isEditing ? (
                <div className="space-y-4">
                  <Textarea
                    value={editedScript}
                    onChange={(e) => setEditedScript(e.target.value)}
                    className="min-h-[400px] font-mono text-sm bg-background/50"
                  />
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setIsEditing(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveEdit}>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-background/50 rounded-lg p-4 max-h-[500px] overflow-y-auto">
                  <pre className="whitespace-pre-wrap font-mono text-sm text-foreground/90">
                    {narrationOnly && project?.script 
                      ? extractNarration(project.script) 
                      : (project?.script || "No script available")}
                  </pre>
                </div>
              )}
            </Card>

            {/* Version History */}
            {versions.length > 0 && (
              <Card className="p-4 bg-card/50 border-border/50">
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center gap-2 w-full text-left">
                    <History className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Version History ({versions.length})</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3">
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {versions.map((v) => (
                        <div key={v.id} className="flex items-center justify-between p-2 rounded-lg bg-background/50 border border-border/50 text-sm">
                          <div>
                            <span className="font-medium">v{v.version_number}</span>
                            {v.label && <span className="text-muted-foreground ml-2">— {v.label}</span>}
                            <span className="text-xs text-muted-foreground ml-2">
                              {new Date(v.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => restoreVersion(v)} className="text-xs h-7">
                            Restore
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            )}

            {/* AI Edit Section */}
            <Card className="p-4 bg-card/50 border-border/50">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="font-display font-semibold">AI Script Editor</h3>
                {previousScript && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleUndo}
                    className="ml-auto text-muted-foreground hover:text-foreground"
                  >
                    <Undo2 className="w-4 h-4 mr-1" />
                    Undo
                  </Button>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Describe the changes you want and AI will edit the script for you.
              </p>
              <div className="flex gap-2 mb-3">
                <Textarea
                  placeholder="e.g., Make the intro more dramatic, add a stronger hook, shorten the middle section..."
                  value={aiEditPrompt}
                  onChange={(e) => setAiEditPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !isAiEditing && aiEditPrompt.trim()) {
                      e.preventDefault();
                      handleAiEdit();
                    }
                  }}
                  disabled={isAiEditing}
                  className="flex-1 min-h-[80px]"
                  rows={3}
                />
                <Button
                  onClick={handleAiEdit}
                  disabled={isAiEditing || !aiEditPrompt.trim()}
                  className="self-end"
                >
                  {isAiEditing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Editing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Apply
                    </>
                  )}
                </Button>
              </div>
              {/* Suggested edit chips */}
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_EDITS.map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setAiEditPrompt(suggestion)}
                    className="px-3 py-1.5 text-xs rounded-full bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors border border-border/50"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </Card>

            {/* Export to Monetisation AI */}
            <Card className="p-4 bg-gradient-to-r from-green-500/5 to-emerald-500/5 border-green-500/20">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <ArrowUpRight className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Turn This Into a Product</p>
                    <p className="text-xs text-muted-foreground">
                      Send this script to Monetisation AI to create courses, eBooks, or digital products
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 border-green-500/30 hover:bg-green-500/10"
                  onClick={() => {
                    const scriptContent = encodeURIComponent(project?.script?.slice(0, 2000) || "");
                    navigate(`/app/monetization-ai/new?fromScript=${scriptContent}&title=${encodeURIComponent(project?.title || "")}`);
                  }}
                >
                  Turn Into a Product
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </Card>

            {/* YouTube Titles A/B Testing */}
            <Card className="p-4 md:p-6 bg-card/50 border-border/50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {generatedTitles.length > 0 && <Check className="w-4 h-4 text-green-500" />}
                  <LetterText className="w-5 h-5 text-primary" />
                  <h3 className="font-display font-semibold">YouTube Titles (A/B Testing)</h3>
                  {generatedTitles.length > 0 && (
                    <Badge variant="outline" className="text-[10px] text-green-500 border-green-500/30">Generated</Badge>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGenerateTitles}
                  disabled={isGeneratingTitles || !project?.script}
                >
                  {isGeneratingTitles ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" />{generatedTitles.length > 0 ? "Regenerate" : "Generate Titles"}</>
                  )}
                </Button>
              </div>
              {generatedTitles.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Generate 3 optimized title variations to A/B test on YouTube.
                </p>
              )}
              {generatedTitles.length > 0 && (
                <Collapsible open={titlesOpen} onOpenChange={setTitlesOpen}>
                  <CollapsibleTrigger className="text-sm text-primary hover:underline mb-2">
                    {titlesOpen ? "Hide titles" : "Show generated titles"}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="space-y-3 mt-2">
                      {generatedTitles.map((item, i) => (
                        <div
                          key={i}
                          className="flex items-start justify-between gap-3 p-3 rounded-lg bg-background/50 border border-border/50"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-[10px] capitalize">
                                {item.strategy}
                              </Badge>
                            </div>
                            <p className="font-medium text-sm">{item.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">{item.reasoning}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="flex-shrink-0 h-8 w-8"
                            onClick={() => handleCopyTitle(item.title)}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </Card>

            {/* YouTube Description */}
            <Card className="p-4 md:p-6 bg-card/50 border-border/50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {generatedDescription && <Check className="w-4 h-4 text-green-500" />}
                  <FileText className="w-5 h-5 text-primary" />
                  <h3 className="font-display font-semibold">YouTube Description</h3>
                  {generatedDescription && (
                    <Badge variant="outline" className="text-[10px] text-green-500 border-green-500/30">Generated</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {generatedDescription && (
                    <Button variant="ghost" size="sm" onClick={handleCopyDescription}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateDescription}
                    disabled={isGeneratingDescription || !project?.script}
                  >
                    {isGeneratingDescription ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" />{generatedDescription ? "Regenerate" : "Generate Description"}</>
                    )}
                  </Button>
                </div>
              </div>
              {!generatedDescription && (
                <p className="text-sm text-muted-foreground">
                  Generate an SEO-optimized YouTube video description with timestamps and hashtags.
                </p>
              )}
              {generatedDescription && (
                <Collapsible open={descriptionOpen} onOpenChange={setDescriptionOpen}>
                  <CollapsibleTrigger className="text-sm text-primary hover:underline mb-2">
                    {descriptionOpen ? "Hide description" : "Show generated description"}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="bg-background/50 rounded-lg p-4 max-h-[400px] overflow-y-auto border border-border/50 mt-2">
                      <pre className="whitespace-pre-wrap text-sm text-foreground/90 font-sans">
                        {generatedDescription}
                      </pre>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </Card>

            <Button
              variant="hero"
              size="lg"
              className="w-full gap-2"
              onClick={() => navigate(`/app/nexis-ai/project/${projectId}/visual-style`)}
            >
              Generate Visual Prompts
              <ArrowRight className="w-5 h-5" />
            </Button>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
